/* istanbul ignore file */ /* this file is not used or will be deleted in other branches */
// @ts-nocheck
import React, { FC, useState, useEffect, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import clsx from 'clsx';
import moment from 'moment';
import { uuid } from 'uuidv4';
import { FieldWrapper, AddButton } from '@td-client-frontend/common';
import Pill from './components/Pill';
import { PlanError, PlansOption, Plan } from './interfaces/PlanIntrfaces';

import Modal from '../../Modal/NewModal';
import AddNewPlans from './modalBody/AddNewPlans';
import PlansHeader from './components/PlansHeader';
import EditPlans from './modalBody/EditPlans';
import MultiEditPlans from './modalBody/MultiEditPlans';
import {
  checkCodeRequired,
  checkRequired,
  deletePlan,
  editPlan,
  getCodeValueError,
  getDuplicatePlans,
  separatePlans
} from '../../../utils/plansModalUtils';

const actionOptions: PlansOption[] = [
  { label: 'Edit Effective Date', value: 'effective' },
  { label: 'Edit Termination Date', value: 'termination' },
  { label: 'Delete', value: 'delete' }
];

interface PublishPlan {
  code: string;
  start_dt: string;
  end_dt: string | null;
}

interface MultiInputProps {
  label: string;
  name: string;
  value: Plan[];
  isError: boolean;
  error: PlanError;
  labelBtn: string;
  onChange: (toPublishPlan: PublishPlan[] | any) => void;
}

export const CodeMultiInput: FC<MultiInputProps> = ({ label, name, value, isError, error }) => {
  const { change } = useFormApi();

  const [showModal, setShowModal] = useState<boolean>(false);

  const modalTitleValue: string = label === 'Plan code*' ? 'plan' : 'benefit';

  const [currentPlansState, setCurrentPlansState] = useState<Plan[]>([...value]);

  const [showAddNewPlan, setShowAddNewPlan] = useState<boolean>(false);
  const [newPlans, setNewPlans] = useState<Plan[]>([]);
  const [newPlanErrors, setNewPlanErrors] = useState<PlanError[]>([]);

  const [editedPlansState, setEditedPlansState] = useState<Plan[]>([...value, ...newPlans]);
  const [editedPlansError, setEditedPlansError] = useState<PlanError[]>([]);

  const [selectDisabled, setSelectDisabled] = useState<boolean>(true);

  const [showCheckbox, setShowCheckbox] = useState<boolean>(false);
  const [checkedPlansId, setCheckedPlansId] = useState<string[]>([]);
  const [checkedAllStatus, setCheckedAllStatus] = useState<boolean>(false);

  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const [showMultiEdit, setShowMultiEdit] = useState<boolean>(false);
  const [multiDateValue, setMultiDateValue] = useState<string>('');
  const [multiDateValueError, setMultiDateValueError] = useState<string>('');

  const [coloredPlansId, setColoredPlansId] = useState<string[]>([]);

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const [validatePlans] = useDebounce<Plan[]>(showAddNewPlan ? newPlans : editedPlansState, 500);

  const isDots: boolean = value.length > 5;

  const pill: Plan[] = editedPlansState.filter((_, index) => index < 5);

  const addNewPlan = (): void => {
    const newPlan: Plan = {
      id: uuid(),
      code: null,
      start_dt: null,
      end_dt: null
    };
    setNewPlans((prev) => [...prev, newPlan]);
  };

  const onEditPlan = useCallback(
    (id: string, key: string, value: string): void => {
      editPlan(id, key, value, editedPlansState, setEditedPlansState);
    },
    [editedPlansState]
  );

  const onEditNewPlan = useCallback(
    (id: string, key: string, value: string): void => {
      editPlan(id, key, value, newPlans, setNewPlans);
    },
    [newPlans]
  );

  const multiEditPlans = useCallback(
    (key: string, value: string): void => {
      const editedPlan = editedPlansState.map((plan) => {
        if (checkedPlansId.some((checkedPlan) => checkedPlan === plan.id)) {
          return { ...plan, [key]: value };
        }
        return plan;
      });

      setEditedPlansState(editedPlan);
      setColoredPlansId(checkedPlansId);
    },
    [checkedPlansId, editedPlansState]
  );

  const onDeletePlan = useCallback(
    (planId: string, isItInitialFields: boolean): void => {
      if (!isItInitialFields) {
        deletePlan(planId, editedPlansState, setEditedPlansState);
      } else {
        const currentDate = new Date();
        onEditPlan(planId, 'end_dt', moment(currentDate).format('yyyy-MM-DD'));
      }
    },
    [editedPlansState, onEditPlan]
  );

  const onDeleteNewPlan = useCallback(
    (planId: string): void => {
      deletePlan(planId, newPlans, setNewPlans);
    },
    [newPlans]
  );

  const multiDeletePlans = useCallback((): void => {
    const clearedPlans: Plan[] = editedPlansState.reduce((acc: Plan[], plan: Plan) => {
      const remove = checkedPlansId.some((checkedPlan) => checkedPlan === plan.id);
      if (remove && plan.beCode) {
        const currentDate = new Date();
        acc.push({
          ...plan,
          end_dt: moment(currentDate).utc().toISOString()
        });
      } else if (!remove) {
        acc.push(plan);
      }
      return acc;
    }, []);
    setEditedPlansState(clearedPlans);
    setSelectedValue(null);
    setShowConfirmModal(false);
  }, [checkedPlansId, editedPlansState]);

  const setCheckedAllPlans = useCallback((): void => {
    if (checkedAllStatus) {
      setCheckedPlansId([]);
    } else {
      setCheckedPlansId(editedPlansState.map((plan) => plan.id));
    }
  }, [checkedAllStatus, editedPlansState]);

  const changeChekedStatus = useCallback(({ newCheckedValue, plan }) => {
    if (newCheckedValue === true) {
      setCheckedPlansId((prev) => [...prev, plan.id]);
    } else {
      setCheckedPlansId((prev) => prev.filter((planItem) => planItem !== plan.id));
    }
  }, []);

  const savePlansHandler = (): void => {
    const separetedPlans = separatePlans(editedPlansState, false);
    const codeError = getDuplicatePlans(separetedPlans);
    const requiredError = checkRequired(editedPlansState);
    const error = [...codeError, ...requiredError];
    if (error.length) {
      setEditedPlansError(error);
    } else if (!editedPlansError.length) {
      const separetedPlansWithNewId: Plan[] = separatePlans(editedPlansState, true);

      const formattedPlans = separetedPlansWithNewId.map(({ code, start_dt, end_dt }) => ({
        code: code!.trim(),
        end_dt: end_dt,
        start_dt: start_dt
      }));

      const newSetPlans = [...formattedPlans];

      change(name, newSetPlans);
      setCurrentPlansState(separetedPlansWithNewId);
      setEditedPlansState(separetedPlansWithNewId);
      setStateToDefault();
    }
  };

  const saveNewPlansHandler = (): void => {
    const separatedPlans = separatePlans(newPlans, true);
    const requiredErrors = checkCodeRequired(newPlans);
    if (requiredErrors.length) {
      setNewPlanErrors(requiredErrors);
    } else if (!newPlanErrors.length) {
      setEditedPlansState((prev) => [...prev, ...separatedPlans]);
      setNewPlans([]);
      setShowAddNewPlan(false);
    }
  };

  const saveMultiEditedDate = useCallback((): void => {
    if (!multiDateValue) {
      setMultiDateValueError(`The ${selectedValue} date is required !`);
    } else {
      if (selectedValue === 'effective') {
        multiEditPlans('start_dt', multiDateValue);
      } else {
        multiEditPlans('end_dt', multiDateValue);
      }
      setSelectedValue(null);
      setShowMultiEdit(false);
      setMultiDateValue('');
      setMultiDateValueError('');
    }
  }, [multiDateValue, selectedValue, multiEditPlans]);

  const saveBtnHandler = (): void => {
    if (showAddNewPlan) {
      saveNewPlansHandler();
    } else if (showMultiEdit) {
      saveMultiEditedDate();
    } else {
      savePlansHandler();
    }
  };

  const showModalHandler = useCallback((): void => {
    setShowModal(true);
    if (!value.length && !editedPlansState.length) {
      setShowAddNewPlan(true);
      if (!newPlans.length) {
        addNewPlan();
      }
    } else {
      setShowAddNewPlan(false);
    }
  }, [value.length, editedPlansState.length, newPlans.length]);

  const addNewPlanBtnHandler = useCallback((): void => {
    if (showAddNewPlan) {
      addNewPlan();
    } else {
      setShowAddNewPlan(true);
      addNewPlan();
    }
  }, [showAddNewPlan]);

  const setStateToDefault = (): void => {
    setShowModal(false);
    setShowAddNewPlan(false);
    setNewPlans([]);
    setNewPlanErrors([]);
    setEditedPlansError([]);
    setSelectDisabled(true);
    setCheckedPlansId([]);
    setSelectedValue(null);
    setMultiDateValue('');
    setMultiDateValueError('');
    setColoredPlansId([]);
    setShowMultiEdit(false);
  };

  const onAddNewModalClose = useCallback(() => {
    if (!editedPlansState.length) {
      setShowModal(false);
      setNewPlans([]);
      setNewPlanErrors([]);
    } else {
      setShowAddNewPlan(false);
      setNewPlans([]);
      setNewPlanErrors([]);
    }
  }, [editedPlansState.length]);

  const onMultiEditClose = () => {
    setShowMultiEdit(false);
    setMultiDateValue('');
    setMultiDateValueError('');
    setSelectedValue(null);
  };

  const onEditClose = useCallback(() => {
    setEditedPlansState(currentPlansState);
    setShowModal(false);
  }, [currentPlansState]);

  const onConfirmModalClose = (): void => {
    setSelectedValue(null);
    setShowConfirmModal(false);
  };

  const onClose = useCallback((): void => {
    if (showAddNewPlan) onAddNewModalClose();
    if (showMultiEdit) onMultiEditClose();
    if (!showAddNewPlan && !showMultiEdit) onEditClose();
    setCheckedPlansId([]);
    setColoredPlansId([]);
  }, [onEditClose, onAddNewModalClose, showAddNewPlan, showMultiEdit]);

  useEffect(() => {
    if ((showAddNewPlan || showMultiEdit) && coloredPlansId.length) {
      setColoredPlansId([]);
    }
  }, [showAddNewPlan, showMultiEdit, coloredPlansId.length]);

  useEffect(() => {
    if (editedPlansState.length === checkedPlansId.length) {
      setCheckedAllStatus(true);
    } else {
      setCheckedAllStatus(false);
    }
  }, [editedPlansState.length, checkedPlansId.length, checkedAllStatus]);

  useEffect(() => {
    if (checkedPlansId.length) {
      setSelectDisabled(false);
    } else if (!selectDisabled) {
      setSelectDisabled(true);
    }
  }, [checkedPlansId, selectDisabled]);

  useEffect(() => {
    if (editedPlansState.length >= 2) {
      setShowCheckbox(true);
    } else if (showCheckbox) {
      setShowCheckbox(false);
    }
  }, [editedPlansState.length, showCheckbox]);

  useEffect(() => {
    if (selectedValue) {
      if (selectedValue === 'delete') {
        setShowConfirmModal(true);
      } else {
        setShowMultiEdit(true);
        setMultiDateValueError('');
      }
    }
  }, [selectedValue]);

  useEffect(() => {
    const separatedEditedPlan = separatePlans(validatePlans);
    if (showAddNewPlan) {
      const duplicatedCodeError = getDuplicatePlans([...editedPlansState, ...separatedEditedPlan]);
      const codeValueError = getCodeValueError([...editedPlansState, ...separatedEditedPlan]);
      setNewPlanErrors([...codeValueError, ...duplicatedCodeError]);
    } else {
      const duplicatedCodeError = getDuplicatePlans(separatedEditedPlan);
      const codeValueError = getCodeValueError(separatedEditedPlan);
      setEditedPlansError([...codeValueError, ...duplicatedCodeError]);
    }
  }, [validatePlans, showAddNewPlan, editedPlansState]);

  useEffect(() => {
    setCurrentPlansState(value);
    setEditedPlansState(value);
  }, [value]);

  return (
    <>
      <Modal
        isOpen={showModal}
        onClose={onClose}
        header={`${showAddNewPlan ? 'Add' : 'Edit'} ${modalTitleValue} codes`}
        actions={[
          { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onClose },
          {
            title: 'Save',
            kind: 'primary',
            autoClose: false,
            onClick: saveBtnHandler
          }
        ]}
        size="medium"
      >
        <Modal
          isOpen={showConfirmModal}
          onClose={onConfirmModalClose}
          header=""
          actions={[
            { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onConfirmModalClose },
            {
              title: 'Delete',
              kind: 'primary',
              autoClose: false,
              onClick: multiDeletePlans
            }
          ]}
          size="prompt"
        >
          <h6 className="px-3 font-effra text-2xl text-black-default font-medium">
            Are you sure you wish to delete these codes ?
          </h6>
        </Modal>
        <section className="w-full p-6 font-bold">
          <div className="w-full">
            <h2 className="pb-4 font-effra text-base font-medium">
              Enter in a code and select an effective date. To bulk add please enter into the box and separate using a
              semi-colon - Example: A01;A03;A05;
            </h2>
          </div>
          <PlansHeader
            showAddNewPlan={showAddNewPlan}
            actionOptions={actionOptions}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            selectDisabled={selectDisabled}
            showCheckbox={showCheckbox}
            checkedAllStatus={checkedAllStatus}
            setCheckedAllStatus={setCheckedAllPlans}
            showMultiEdit={showMultiEdit}
            multiDateValue={multiDateValue}
            setMultiDateValue={setMultiDateValue}
            multiDateValueError={multiDateValueError}
            saveMultiEditedDate={saveMultiEditedDate}
            cancelHandler={onMultiEditClose}
          />
          <div
            className={clsx(
              showAddNewPlan ? 'h-auto' : 'h-64',
              !showMultiEdit && 'border-b-1',
              'overflow-auto border-gray-20 max-h-64'
            )}
            style={{ maxHeight: '16rem' }}
          >
            {showMultiEdit
              ? editedPlansState
                  .filter((plan) => checkedPlansId.includes(plan.id))
                  .map((plan) => <MultiEditPlans key={plan.id} plan={plan} />)
              : showAddNewPlan
              ? newPlans.map((plan) => {
                  return (
                    <AddNewPlans
                      key={plan.id}
                      plan={plan}
                      newPlanErrors={newPlanErrors}
                      deletePlan={onDeleteNewPlan}
                      editFields={onEditNewPlan}
                      showDelete={newPlans.length > 1}
                    />
                  );
                })
              : editedPlansState.map((plan) => {
                  const checkedPlan = checkedPlansId.some((checkedPlan) => checkedPlan === plan.id);
                  const coloredPlan = coloredPlansId.some((eidtedPlan) => eidtedPlan === plan.id);

                  return (
                    <EditPlans
                      key={plan.id}
                      plan={plan}
                      fieldsErrors={editedPlansError}
                      deletePlan={onDeletePlan}
                      editFields={onEditPlan}
                      bgColor={coloredPlan ? 'bg-green-20' : 'bg-white'}
                      showCheckbox={showCheckbox}
                      checkedPlan={checkedPlan}
                      setPlanChecked={changeChekedStatus}
                      disabledField={!!plan.beCode}
                    />
                  );
                })}
          </div>

          {!showMultiEdit && (
            <div className="flex w-full items-center justify-between">
              <AddButton tabIndex={-1} label="Add new" onClick={addNewPlanBtnHandler} />
            </div>
          )}
        </section>
      </Modal>
      <FieldWrapper
        label={label}
        isError={isError}
        error={error}
        labelBtnType={value.length ? 'Edit' : 'Add'}
        hideIcon={value.length}
        fieldLabelBtnClasses="ml-1 pt-0 h-4 border-b-0"
        labelBtn={showModalHandler}
        labelClasses={clsx(
          'block mb-2 text-sm font-bold tracking-wide font-effra text-black-default',
          !value.length && 'mr-5'
        )}
      >
        <div className="relative h-12">
          <div
            className={clsx(
              'form-field flex items-center overflow-none absolute top-0 h-auto min-h-12 bg-white p-3 pl-0',
              'border-none resize-none scroll-none',
              isError && 'form-field-error'
            )}
            tabIndex={-1}
          >
            {!value.length ? (
              <h3 className="text-left text-gray-40 text-sm font-medium font-effra">No plan code</h3>
            ) : (
              pill.map((pill) => {
                return <Pill key={pill.code}>{pill.code?.slice(0, 4)}</Pill>;
              })
            )}
            {isDots && <span className="ml-2 w-6 mt-1.5 mr-3">&hellip;</span>}
          </div>
        </div>
      </FieldWrapper>
    </>
  );
};
